import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import { Plus, Trash2, Loader2, Download, Filter, Search, ExternalLink,
  Package, DollarSign, Users, TrendingUp, ChevronRight, X, Building2 } from 'lucide-react';

const CAT_BG = { BULK: 'var(--tone-blue-bg)', TAGGED: 'var(--tone-amber-bg)' };
const CAT_FG = { BULK: 'var(--tone-blue-text)', TAGGED: 'var(--tone-amber-text)' };

function TypeDrilldown({ projectId, code, onClose }) {
  const [data, setData] = useState(null);
  const [drillMci, setDrillMci] = useState(null);
  useEffect(() => {
    if (!code) return;
    api.get(`/v1/projects/${projectId}/mci/material-types/${encodeURIComponent(code)}/mcis`)
      .then(r => setData(r.data)).catch(() => toast.error('Load failed'));
  }, [code, projectId]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose} data-testid="type-drilldown">
      <div className="w-full max-w-3xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}>
        <div className="sticky top-0 z-10 px-5 py-3 border-b flex items-center justify-between"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div>
            <div className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Material Type Drilldown</div>
            <h3 className="text-base font-bold" style={{ color: 'var(--tone-navy-text)' }}>{code}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="p-4">
          {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10" /> : (
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
              <table className="w-full text-[11px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>MCI</th>
                    <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Description</th>
                    <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Vendor</th>
                    <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>PO · Line</th>
                    <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Value</th>
                    <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Status</th>
                    <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Progress</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.mcis || []).map(m => (
                    <tr key={m.mciLineNumber} onClick={() => setDrillMci(m.mciLineNumber)}
                      className="cursor-pointer hover:bg-black/5"
                      style={{ borderTop: '1px solid var(--border-color)' }}
                      data-testid={`type-mci-${m.mciLineNumber}`}>
                      <td className="px-2 py-1.5 font-mono font-bold" style={{ color: 'var(--tone-navy-text)' }}>{m.mciLineNumber}</td>
                      <td className="px-2 py-1.5 max-w-[260px] truncate" style={{ color: 'var(--text-primary)' }}>{m.materialDescription}</td>
                      <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{m.vendor || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        {m.poNumber ? `${m.poNumber} · L${m.poLineItem || '—'}` : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: '#F26B1F' }}>
                        {m.poValue ? `$${Math.round((m.poValue || 0) / 1000)}k` : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-center text-[10px]">{m.overallStatus || '—'}</td>
                      <td className="px-2 py-1.5 text-center text-[10px]">{Math.round(m.overallProgress || 0)}%</td>
                      <td className="px-2 py-1.5"><ExternalLink className="w-3 h-3" style={{ color: 'var(--tone-navy-text)' }} /></td>
                    </tr>
                  ))}
                  {(data.mcis || []).length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                      No MCIs use this material type yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {drillMci && <MciPackageDrawer projectId={projectId} mci={drillMci} onClose={() => setDrillMci(null)} />}
      </div>
    </div>
  );
}

export default function MaterialTypesConfig({ projectId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/mci/material-types/stats`);
      setStats(data);
    } catch (e) { toast.error('Failed to load material type stats'); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const seedDefaults = async () => {
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/mci/material-types/seed-defaults`);
      toast.success(data.message); fetchStats();
    } catch (e) { toast.error('Failed to seed'); }
  };

  const filtered = useMemo(() => {
    if (!stats) return [];
    const s = search.trim().toLowerCase();
    return (stats.types || []).filter(t =>
      (filter === 'ALL' || (t.category || '').toUpperCase() === filter) &&
      (!s || t.materialTypeCode.toLowerCase().includes(s) || (t.materialTypeName || '').toLowerCase().includes(s))
    );
  }, [stats, filter, search]);

  const kpis = useMemo(() => {
    if (!stats) return {};
    const arr = stats.types || [];
    return {
      types: arr.length,
      used: arr.filter(t => t.mciCount > 0).length,
      unused: arr.filter(t => t.mciCount === 0).length,
      totalMcis: stats.totalMcis || 0,
      totalValue: arr.reduce((s, t) => s + (t.totalValue || 0), 0),
    };
  }, [stats]);

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;

  const btnActive = { background: '#1E4E8C', color: '#ffffff' };
  const btnIdle   = { background: 'var(--card-bg)', color: 'var(--text-muted)' };

  return (
    <div className="space-y-4" data-testid="material-types-config">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: 'Material Types', v: kpis.types,     c: 'var(--tone-navy-text)', I: Package },
          { l: 'In Use',         v: kpis.used,      c: '#10B981', I: TrendingUp },
          { l: 'Unused',         v: kpis.unused,    c: '#F59E0B', I: Filter },
          { l: 'Total MCIs',     v: kpis.totalMcis, c: 'var(--tone-navy-text)', I: Package },
          { l: 'PO Exposure',    v: `$${((kpis.totalValue||0)/1e6).toFixed(1)}M`, c: '#F26B1F', I: DollarSign },
        ].map((k, i) => {
          const I = k.I;
          return (
            <div key={i} className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
              data-testid={`mt-kpi-${i}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>
                <I className="w-3 h-3" style={{ color: k.c }} />{k.l}
              </div>
              <div className="text-xl font-bold mt-1" style={{ color: k.c }}>{k.v}</div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search code or name" data-testid="mt-search"
            className="pl-8 pr-3 py-1.5 rounded-lg text-xs border w-64"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
          {['ALL', 'BULK', 'TAGGED'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className="px-3 py-1.5 text-xs font-semibold transition-colors"
              style={filter === f ? btnActive : btnIdle}
              data-testid={`mt-filter-${f.toLowerCase()}`}>{f}</button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={seedDefaults} className="ml-auto" data-testid="mt-seed">
          <Download className="w-3.5 h-3.5 mr-1" /> Seed Defaults
        </Button>
      </div>

      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Code</th>
              <th className="px-2 py-2 text-left uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Name</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Category</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>MCIs</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Vendors</th>
              <th className="px-2 py-2 text-right uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>PO Value</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Avg Planned LT</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Avg Actual LT</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>Variance</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}>OTD %</th>
              <th className="px-2 py-2 text-center uppercase text-[10px] font-bold" style={{ color: 'var(--text-muted)' }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => {
              const cat = (t.category || '').toUpperCase();
              return (
                <tr key={t.materialTypeCode} onClick={() => t.mciCount > 0 && setSelected(t.materialTypeCode)}
                  className={t.mciCount > 0 ? "cursor-pointer hover:bg-black/5" : ""}
                  style={{ borderTop: '1px solid var(--border-color)', opacity: t.mciCount === 0 ? 0.55 : 1 }}
                  data-testid={`mt-row-${t.materialTypeCode}`}>
                  <td className="px-2 py-1.5 font-mono font-bold" style={{ color: 'var(--tone-navy-text)' }}>{t.materialTypeCode}</td>
                  <td className="px-2 py-1.5" style={{ color: 'var(--text-primary)' }}>{t.materialTypeName}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                      style={{ background: CAT_BG[cat] || 'var(--card-bg-alt)', color: CAT_FG[cat] || 'var(--text-muted)' }}>
                      {cat || '—'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{t.mciCount}</td>
                  <td className="px-2 py-1.5 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{t.vendorCount}</td>
                  <td className="px-2 py-1.5 text-right font-mono" style={{ color: '#F26B1F' }}>
                    {t.totalValue ? `$${((t.totalValue||0)/1e6).toFixed(1)}M` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{t.avgPlannedLT ? `${t.avgPlannedLT}d` : '—'}</td>
                  <td className="px-2 py-1.5 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{t.avgActualLT ? `${t.avgActualLT}d` : '—'}</td>
                  <td className="px-2 py-1.5 text-center font-mono"
                    style={{ color: t.variance > 0 ? '#EF4444' : t.variance < 0 ? '#10B981' : 'var(--text-muted)' }}>
                    {t.variance ? `${t.variance > 0 ? '+' : ''}${t.variance}d` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono font-bold"
                    style={{ color: t.otdPercent >= 80 ? '#10B981' : t.otdPercent >= 60 ? '#F59E0B' : t.otdPercent > 0 ? '#EF4444' : 'var(--text-muted)' }}>
                    {t.otdPercent ? `${t.otdPercent}%` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {t.mciCount > 0 && <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--tone-navy-text)' }} />}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                No material types match the current filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <TypeDrilldown projectId={projectId} code={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
